import type { AdminMapSummary } from '../../../spacetime/SpaceTimeClient';
import type { AdminGridShape, AdminToolSelection } from '../types';
import { buttonStyle, createAdminInput, createMapSelect, createSectionLabel } from './common';

interface MapUtilitiesState {
  isSavingFrontendMapAsset: boolean;
  isUploadingMapJson: boolean;
  isReloadingMaps: boolean;
  mapCatalog: AdminMapSummary[];
  createMapId: string;
  createMapName: string;
  createMapWidth: string;
  createMapHeight: string;
  createMapGridType: AdminGridShape;
  deleteMapId: string;
  selectedTool: AdminToolSelection | null;
  currentMapId: string;
}

interface MapUtilitiesActions {
  render: () => void;
  setMapCatalogStatus: (value: string) => void;
  setSavingFrontendMapAsset: (value: boolean) => void;
  setUploadingMapJson: (value: boolean) => void;
  setReloadingMaps: (value: boolean) => void;
  setCreateMapId: (value: string) => void;
  setCreateMapName: (value: string) => void;
  setCreateMapWidth: (value: string) => void;
  setCreateMapHeight: (value: string) => void;
  setCreateMapGridType: (value: AdminGridShape) => void;
  setDeleteMapId: (value: string) => void;
  setSelectedTool: (tool: AdminToolSelection | null) => void;
  onSaveFrontendMapAsset: () => Promise<string>;
  onUploadMapJson: () => Promise<boolean>;
  onWipeAndReloadMaps: () => Promise<void>;
  createMap: (input: { mapId: string; name: string; width: number; height: number; gridType: AdminGridShape }) => Promise<void>;
  deleteMap: (mapId: string) => Promise<void>;
  refreshMapCatalog: () => Promise<void>;
}

const GRID_SHAPE_OPTIONS: AdminGridShape[] = ['hex', 'square', 'triangle'];

export function renderMapToolActionsSection(
  state: MapUtilitiesState,
  actions: MapUtilitiesActions
): HTMLDivElement {
  const mapToolsSection = createSectionLabel('Map Tools');

  const saveBtn = document.createElement('button');
  saveBtn.type = 'button';
  saveBtn.textContent = state.isSavingFrontendMapAsset ? 'Exporting Map...' : 'Export Map JSON';
  Object.assign(saveBtn.style, buttonStyle('#0f766e'));
  saveBtn.disabled = state.isSavingFrontendMapAsset;
  saveBtn.addEventListener('click', async () => {
    actions.setSavingFrontendMapAsset(true);
    actions.setMapCatalogStatus('Exporting current map JSON...');
    actions.render();
    try {
      const filename = await actions.onSaveFrontendMapAsset();
      actions.setMapCatalogStatus(`Exported map JSON to ${filename}`);
    } catch (error) {
      actions.setMapCatalogStatus(error instanceof Error ? error.message : 'Failed to export the current map JSON');
    } finally {
      actions.setSavingFrontendMapAsset(false);
      actions.render();
    }
  });

  const reloadMapsBtn = document.createElement('button');
  reloadMapsBtn.type = 'button';
  reloadMapsBtn.textContent = state.isReloadingMaps ? 'Reloading Maps...' : 'Wipe And Reload Maps';
  Object.assign(reloadMapsBtn.style, buttonStyle('#2563eb'));
  reloadMapsBtn.disabled = state.isReloadingMaps;
  reloadMapsBtn.addEventListener('click', async () => {
    if (!window.confirm('Are you sure you want to wipe the current map data and reload it from the asset JSON files?')) {
      return;
    }
    actions.setReloadingMaps(true);
    actions.render();
    try {
      await actions.onWipeAndReloadMaps();
      actions.setMapCatalogStatus('Map data wiped and reloaded.');
    } finally {
      actions.setReloadingMaps(false);
      actions.render();
    }
  });

  mapToolsSection.appendChild(saveBtn);

  const uploadBtn = document.createElement('button');
  uploadBtn.type = 'button';
  uploadBtn.textContent = state.isUploadingMapJson ? 'Uploading Map JSON...' : 'Upload Map JSON';
  Object.assign(uploadBtn.style, buttonStyle('#7c3aed'));
  uploadBtn.disabled = state.isUploadingMapJson;
  uploadBtn.addEventListener('click', async () => {
    actions.setUploadingMapJson(true);
    actions.setMapCatalogStatus('Uploading map JSON to live backend state...');
    actions.render();
    try {
      const uploaded = await actions.onUploadMapJson();
      if (uploaded) {
        await actions.refreshMapCatalog();
      }
      actions.setMapCatalogStatus(uploaded
        ? 'Uploaded map JSON into live backend state.'
        : 'Map JSON upload canceled.');
    } catch (error) {
      actions.setMapCatalogStatus(error instanceof Error ? error.message : 'Failed to upload map JSON');
    } finally {
      actions.setUploadingMapJson(false);
      actions.render();
    }
  });

  mapToolsSection.appendChild(reloadMapsBtn);
  mapToolsSection.appendChild(uploadBtn);
  return mapToolsSection;
}

export function renderCreateMapSection(
  state: MapUtilitiesState,
  actions: MapUtilitiesActions
): HTMLDivElement {
  const createMapSection = createSectionLabel('Create Map');
  const createIdInput = createAdminInput('Map ID', state.createMapId, actions.setCreateMapId);
  const createNameInput = createAdminInput('Name', state.createMapName, actions.setCreateMapName);
  const createIdField = createIdInput.querySelector('input');
  const createNameField = createNameInput.querySelector('input');
  const sizeRow = document.createElement('div');
  Object.assign(sizeRow.style, { display: 'flex', gap: '8px' });
  const createWidthInput = createAdminInput('Width', state.createMapWidth, actions.setCreateMapWidth, 'number');
  const createHeightInput = createAdminInput('Height', state.createMapHeight, actions.setCreateMapHeight, 'number');
  const createWidthField = createWidthInput.querySelector('input');
  const createHeightField = createHeightInput.querySelector('input');
  sizeRow.appendChild(createWidthInput);
  sizeRow.appendChild(createHeightInput);
  const gridTypeLabel = document.createElement('label');
  Object.assign(gridTypeLabel.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#cbd5e1',
    fontSize: '12px',
    fontWeight: '600',
  });
  gridTypeLabel.textContent = 'Tile Shape';
  const gridTypeSelect = document.createElement('select');
  Object.assign(gridTypeSelect.style, {
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f8fafc',
    padding: '8px 10px',
    fontSize: '13px',
  });
  for (const optionValue of GRID_SHAPE_OPTIONS) {
    const option = document.createElement('option');
    option.value = optionValue;
    option.textContent = optionValue[0].toUpperCase() + optionValue.slice(1);
    option.selected = optionValue === state.createMapGridType;
    gridTypeSelect.appendChild(option);
  }
  gridTypeSelect.addEventListener('change', () => {
    actions.setCreateMapGridType(gridTypeSelect.value as AdminGridShape);
  });
  gridTypeLabel.appendChild(gridTypeSelect);
  const createBtn = document.createElement('button');
  createBtn.type = 'button';
  createBtn.textContent = 'Create Map';
  Object.assign(createBtn.style, buttonStyle('#1d4ed8'));
  createBtn.addEventListener('click', async () => {
    const mapId = createIdField?.value.trim() ?? '';
    const name = createNameField?.value.trim() ?? '';
    const width = Number(createWidthField?.value ?? '');
    const height = Number(createHeightField?.value ?? '');
    const gridType = gridTypeSelect.value as AdminGridShape;
    actions.setCreateMapId(mapId);
    actions.setCreateMapName(name);
    actions.setCreateMapWidth(createWidthField?.value ?? '');
    actions.setCreateMapHeight(createHeightField?.value ?? '');
    actions.setCreateMapGridType(gridType);
    if (!mapId || !name || !Number.isFinite(width) || !Number.isFinite(height)) {
      actions.setMapCatalogStatus('Map ID, name, width, and height are required.');
      actions.render();
      return;
    }
    try {
      await actions.createMap({
        mapId,
        name,
        width,
        height,
        gridType,
      });
      actions.setMapCatalogStatus(`Created map '${mapId}'.`);
      actions.setCreateMapId('');
      actions.setCreateMapName('');
      actions.setCreateMapGridType('hex');
      await actions.refreshMapCatalog();
    } catch (error) {
      actions.setMapCatalogStatus(error instanceof Error ? error.message : 'Failed to create map.');
    } finally {
      actions.render();
    }
  });
  createMapSection.appendChild(createIdInput);
  createMapSection.appendChild(createNameInput);
  createMapSection.appendChild(sizeRow);
  createMapSection.appendChild(gridTypeLabel);
  createMapSection.appendChild(createBtn);
  return createMapSection;
}

export function renderDeleteMapSection(
  state: MapUtilitiesState,
  actions: MapUtilitiesActions
): HTMLDivElement {
  const deleteSection = createSectionLabel('Delete Map');
  const deleteSelect = createMapSelect(state.deleteMapId, actions.setDeleteMapId, state.mapCatalog);
  const deleteBtn = document.createElement('button');
  deleteBtn.type = 'button';
  deleteBtn.textContent = 'Delete Selected Map';
  Object.assign(deleteBtn.style, buttonStyle('#b91c1c'));
  deleteBtn.addEventListener('click', async () => {
    if (!state.deleteMapId) {
      actions.setMapCatalogStatus('Select a map to delete.');
      actions.render();
      return;
    }
    if (!window.confirm(`Delete map '${state.deleteMapId}'?`)) {
      return;
    }
    try {
      await actions.deleteMap(state.deleteMapId);
      if (
        state.currentMapId === state.deleteMapId
        && (state.selectedTool?.kind === 'portal-place' || state.selectedTool?.kind === 'portal-remove')
      ) {
        actions.setSelectedTool(null);
      }
      actions.setMapCatalogStatus(`Deleted map '${state.deleteMapId}'.`);
      actions.setDeleteMapId('');
      await actions.refreshMapCatalog();
    } catch (error) {
      actions.setMapCatalogStatus(error instanceof Error ? error.message : 'Failed to delete map.');
    } finally {
      actions.render();
    }
  });
  deleteSection.appendChild(deleteSelect);
  deleteSection.appendChild(deleteBtn);
  return deleteSection;
}
